import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

/**
 * Flattens a PDF file by rendering each page to a canvas and creating a new PDF
 * This effectively flattens all layers as it creates a new visual representation
 * 
 * @param file The PDF file to flatten
 * @param onProgress Optional callback for progress updates (0-100)
 * @returns Promise resolving to a Blob containing the flattened PDF
 */
export const flattenPdf = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  try {
    // Initialize progress
    if (onProgress) onProgress(0);
    
    // Load the PDF using PDF.js
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdfDoc = await loadingTask.promise;
    
    // Get total pages for progress calculation
    const totalPages = pdfDoc.numPages;
    
    // Create a new PDF document with pdf-lib
    const newPdfDoc = await PDFDocument.create();
    
    // Process each page
    for (let i = 1; i <= totalPages; i++) {
      try {
        // Update progress (50% of progress is for loading/rendering pages)
        if (onProgress) onProgress(Math.round((i - 1) / totalPages * 50));
        
        // Get the page
        const page = await pdfDoc.getPage(i);
        
        // Use a higher scale factor for better text quality
        // 3.0 provides much better text clarity while still maintaining compatibility
        const scaleFactor = 3.0;
        const viewport = page.getViewport({ scale: scaleFactor });
        
        // Create a canvas to render the page
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        // Get the canvas context with better rendering options
        // Use explicit CanvasRenderingContext2D type to avoid TypeScript errors
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
        
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }
        
        // Apply high-quality rendering settings
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Clear the canvas with white background to avoid transparency issues
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Render the page to canvas (this flattens all layers)
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
          // Enable additional rendering options for better quality
          renderInteractiveForms: true
        };
        
        await page.render(renderContext).promise;
        
        // Convert canvas to image with maximum quality
        // Use PNG for text documents as it provides better text clarity
        let imageData: string;
        try {
          // For text documents, PNG provides better quality with no compression artifacts
          // The lack of compression artifacts is crucial for text clarity
          imageData = canvas.toDataURL('image/png');
          
          // If the PNG data URL is too large (over 5MB), fall back to high-quality JPEG
          if (imageData.length > 5 * 1024 * 1024) {
            console.log(`Page ${i}: PNG too large, using high-quality JPEG instead`);
            imageData = canvas.toDataURL('image/jpeg', 0.95); // Use very high quality JPEG
          }
        } catch (canvasError) {
          console.warn(`Page ${i}: PNG conversion failed, falling back to JPEG:`, canvasError);
          // Fall back to high-quality JPEG
          imageData = canvas.toDataURL('image/jpeg', 0.95);
        }
        
        // Update progress (next 40% is for embedding images)
        if (onProgress) onProgress(50 + Math.round((i - 1) / totalPages * 40));
        
        // Add the image to the new PDF
        // Use embedPng regardless of the actual format since the data URL is handled correctly
        const embeddedImage = await newPdfDoc.embedPng(imageData);
        const newPage = newPdfDoc.addPage([viewport.width, viewport.height]);
        
        newPage.drawImage(embeddedImage, {
          x: 0,
          y: 0,
          width: viewport.width,
          height: viewport.height
        });
      } catch (pageError) {
        console.error(`Error processing page ${i}:`, pageError);
        // Continue with next page instead of failing the entire process
      }
    }
    
    // Update progress (last 10% is for saving)
    if (onProgress) onProgress(90);
    
    // Save the new PDF
    const pdfBytes = await newPdfDoc.save();
    
    // Complete progress
    if (onProgress) onProgress(100);
    
    return new Blob([pdfBytes], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error flattening PDF:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to flatten PDF');
  }
};

/**
 * Checks if a PDF might have layers or other complex content that could benefit from flattening
 * This is a simple heuristic check based on file size
 * 
 * @param file The PDF file to check
 * @returns Promise resolving to a boolean indicating if the PDF might have layers
 */
export const checkPdfForLayers = async (file: File): Promise<boolean> => {
  try {
    // Simple heuristic: larger files are more likely to have layers
    // This is not foolproof but provides a reasonable guess
    const SIZE_THRESHOLD = 500 * 1024; // 500KB
    
    if (file.size > SIZE_THRESHOLD) {
      // For larger files, do a more thorough check
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdfDoc = await loadingTask.promise;
      
      // Check the first page for content
      const page = await pdfDoc.getPage(1);
      const operatorList = await page.getOperatorList();
      
      // If the operator list is complex, it might have layers
      return operatorList.fnArray.length > 100;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking PDF for layers:', error);
    return false;
  }
};
